import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import apiClient, { API_ENDPOINTS } from '../utils/api';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const RegisterBox = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #999;
  text-align: center;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px 16px;
  font-size: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  font-size: 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const Button = styled.button`
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c00;
  font-size: 14px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background-color: #efe;
  border: 1px solid #cfc;
  border-radius: 8px;
  color: #0c0;
  font-size: 14px;
  text-align: center;
`;

const TempRegister: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    user_id: '',
    password: '',
    passwordConfirm: '',
    name: '',
    gender: '남자',
    phone: '',
    address: '',
    birth_date: '',
    is_admin: true, // 관리자로 설정
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 유효성 검사
    if (formData.password !== formData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }

    if (formData.password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다');
      return;
    }

    if (formData.user_id.length < 4) {
      setError('사용자 ID는 최소 4자 이상이어야 합니다');
      return;
    }

    setLoading(true);

    try {
      const { passwordConfirm, ...registerData } = formData;
      
      const response = await apiClient.post(API_ENDPOINTS.REGISTER, registerData);

      if (response.data.success) {
        setSuccess('회원가입이 완료되었습니다! 로그인 페이지로 이동합니다...');
        
        // 토큰 저장
        localStorage.setItem('token', response.data.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));

        // 2초 후 관리자 페이지로 이동
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || '회원가입 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <RegisterBox>
        <Title>관리자 회원가입</Title>
        <Subtitle>임시 관리자 계정 생성</Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>사용자 ID</Label>
            <Input
              type="text"
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              placeholder="최소 4자 이상"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>비밀번호</Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="최소 6자 이상, 최대 50자"
              maxLength={50}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>비밀번호 확인</Label>
            <Input
              type="password"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              placeholder="비밀번호를 다시 입력하세요"
              maxLength={50}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>이름</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름을 입력하세요"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>성별</Label>
            <Select name="gender" value={formData.gender} onChange={handleChange}>
              <option value="남자">남자</option>
              <option value="여자">여자</option>
            </Select>
          </InputGroup>

          <InputGroup>
            <Label>전화번호</Label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="010-1234-5678"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>주소 (선택)</Label>
            <Input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="주소를 입력하세요"
            />
          </InputGroup>

          <InputGroup>
            <Label>생년월일</Label>
            <Input
              type="date"
              name="birth_date"
              value={formData.birth_date}
              onChange={handleChange}
              required
            />
          </InputGroup>

          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="is_admin"
              name="is_admin"
              checked={formData.is_admin}
              onChange={handleChange}
            />
            <CheckboxLabel htmlFor="is_admin">관리자 권한 부여</CheckboxLabel>
          </CheckboxGroup>

          <Button type="submit" disabled={loading}>
            {loading ? '처리 중...' : '회원가입'}
          </Button>
        </Form>
      </RegisterBox>
    </Container>
  );
};

export default TempRegister;

